import React, { useCallback, useEffect, useMemo, useRef, useState } from 'react';
import { Link, Navigate } from 'react-router-dom';
import Map, { Layer, MapRef, NavigationControl, Source } from 'react-map-gl/mapbox';
import type { MapMouseEvent } from 'react-map-gl/mapbox';
import MapboxDraw from '@mapbox/mapbox-gl-draw';
import 'mapbox-gl/dist/mapbox-gl.css';
import '@mapbox/mapbox-gl-draw/dist/mapbox-gl-draw.css';
import { MAPBOX_TOKEN } from '@/config/private/mapboxConfig';
import { useIsAdmin } from '@/hooks/useIsAdmin';
import { Env, ENV_COLORS, ErrorBanner, Spinner, getAdminApiUrl } from '../shared';
import { useBusinesses, type Business } from '../businesses/api/useBusinesses';
import { useEditBusiness } from '../businesses/api/useEditBusiness';

type Polygon = { type: 'Polygon'; coordinates: number[][][] };
type Filter = 'all' | 'missing' | 'verified' | 'pending';
type Sort = 'newest' | 'name' | 'nearest';

// Both match put-business; the server is the authority, these only warn early.
const MAX_AREA_M2 = 70686;
const MAX_VERTICES = 100;

// ponytail: one unpaged read of every business; give the map its own slim
// endpoint if the list outgrows a single response.
const ALL_BUSINESSES = 5000;

const ENVS: Env[] = ['prod', 'dev'];

const SAVE_ERRORS: Record<string, string> = {
  INVALID_GEOFENCE: 'That shape is not a valid polygon.',
  GEOFENCE_TOO_FEW_POINTS: 'A geofence needs at least 3 points.',
  GEOFENCE_TOO_MANY_POINTS: `A geofence can have at most ${MAX_VERTICES} points.`,
  GEOFENCE_NOT_CLOSED: 'The polygon is not closed.',
  GEOFENCE_SELF_INTERSECTS: 'The polygon crosses itself. Move the points so no edges cross.',
  GEOFENCE_TOO_LARGE: `The geofence is larger than ${MAX_AREA_M2.toLocaleString()} m².`,
};

function polygonOf(b: Business | undefined): Polygon | null {
  const coordinates = b?.geofence?.geometry?.polygon;
  return Array.isArray(coordinates) && coordinates.length > 0 ? { type: 'Polygon', coordinates } : null;
}

const rad = (deg: number) => (deg * Math.PI) / 180;

/** Spherical area of a [lng, lat] ring in m²; within ~1% of PostGIS geography. */
function ringAreaM2(ring: number[][]): number {
  let sum = 0;
  for (let i = 0; i < ring.length - 1; i++) {
    const [lng1, lat1] = ring[i];
    const [lng2, lat2] = ring[i + 1];
    sum += rad(lng2 - lng1) * (2 + Math.sin(rad(lat1)) + Math.sin(rad(lat2)));
  }
  return Math.abs((sum * 6378137 * 6378137) / 2);
}

function ringContains(ring: number[][], lng: number, lat: number): boolean {
  let inside = false;
  for (let i = 0, j = ring.length - 1; i < ring.length; j = i++) {
    const [xi, yi] = ring[i];
    const [xj, yj] = ring[j];
    if (yi > lat !== yj > lat && lng < ((xj - xi) * (lat - yi)) / (yj - yi) + xi) inside = !inside;
  }
  return inside;
}

export default function GeofencesPage() {
  const isAdmin = useIsAdmin();
  const [env, setEnv] = useState<Env>('prod');
  const [search, setSearch] = useState('');
  const [filter, setFilter] = useState<Filter>('all');
  const [sort, setSort] = useState<Sort>('newest');
  const [center, setCenter] = useState({ lng: -98.5795, lat: 39.8283 });
  const [selectedId, setSelectedId] = useState<string | null>(null);
  const [draft, setDraft] = useState<Polygon | null>(null);
  const [mapLoaded, setMapLoaded] = useState(false);
  const [revertTick, setRevertTick] = useState(0);
  const [cursor, setCursor] = useState('');
  const [place, setPlace] = useState('');
  const [placeError, setPlaceError] = useState<string | null>(null);
  const mapRef = useRef<MapRef>(null);
  const drawRef = useRef<MapboxDraw | null>(null);

  const apiUrl = getAdminApiUrl(env);
  const { businesses, total, loading, error, refetch } = useBusinesses({ limit: ALL_BUSINESSES, apiUrl });
  const { edit, loading: saving, error: saveError } = useEditBusiness();

  const selected = businesses.find(b => b.id === selectedId);
  const originalKey = JSON.stringify(polygonOf(selected));
  const original = useMemo<Polygon | null>(() => JSON.parse(originalKey), [originalKey]);
  const dirty = Boolean(selected) && JSON.stringify(draft) !== originalKey;

  // Only a nearest-first list needs to re-sort as the map moves.
  const sortCenter = sort === 'nearest' ? center : null;
  const visible = useMemo(() => {
    const q = search.trim().toLowerCase();
    const matches = businesses.filter(b => {
      if (q && !`${b.name} ${b.address ?? ''}`.toLowerCase().includes(q)) return false;
      if (filter === 'missing') return !polygonOf(b);
      if (filter === 'verified') return b.verified;
      if (filter === 'pending') return !b.verified;
      return true;
    });
    if (sort === 'name') return matches.sort((a, b) => a.name.localeCompare(b.name));
    if (sortCenter) {
      // Flat-earth distance is enough to rank; venues with no pin go last.
      const scale = Math.cos(rad(sortCenter.lat));
      const distance = (b: Business) =>
        b.location_lat == null || b.location_lng == null
          ? Infinity
          : Math.hypot((Number(b.location_lng) - sortCenter.lng) * scale, Number(b.location_lat) - sortCenter.lat);
      return matches.sort((a, b) => distance(a) - distance(b));
    }
    return matches;
  }, [businesses, search, filter, sort, sortCenter]);

  const points = useMemo(
    () => ({
      type: 'FeatureCollection' as const,
      features: visible
        .filter(b => b.location_lat != null && b.location_lng != null)
        .map(b => ({
          type: 'Feature' as const,
          properties: { id: b.id, missing: !polygonOf(b) },
          geometry: { type: 'Point' as const, coordinates: [Number(b.location_lng), Number(b.location_lat)] },
        })),
    }),
    [visible],
  );

  const fences = useMemo(
    () => ({
      type: 'FeatureCollection' as const,
      features: visible.flatMap(b => {
        const geometry = polygonOf(b);
        return geometry ? [{ type: 'Feature' as const, properties: { id: b.id }, geometry }] : [];
      }),
    }),
    [visible],
  );

  // Seed the draw control with the selected venue's stored polygon.
  useEffect(() => {
    const draw = drawRef.current;
    if (!draw || !mapLoaded) return;
    draw.deleteAll();
    setDraft(original);
    if (!selectedId) {
      draw.changeMode('simple_select');
    } else if (original) {
      const [featureId] = draw.add({ type: 'Feature', properties: {}, geometry: original as any });
      draw.changeMode('direct_select', { featureId });
    } else {
      draw.changeMode('draw_polygon');
    }
  }, [selectedId, original, mapLoaded, revertTick]);

  const handleMapLoad = useCallback(() => {
    const map = mapRef.current?.getMap();
    if (!map) return;
    const draw = new MapboxDraw({ displayControlsDefault: false, controls: { polygon: true, trash: true } });
    map.addControl(draw as any);
    drawRef.current = draw;

    const sync = () => {
      const [feature] = draw.getAll().features;
      setDraft(feature ? (feature.geometry as Polygon) : null);
    };
    // A venue has exactly one polygon: drawing a new one replaces the old.
    map.on('draw.create', (e: MapboxDraw.DrawCreateEvent) => {
      const createdId = String(e.features[0]?.id);
      const stale = draw.getAll().features.map(f => String(f.id)).filter(id => id !== createdId);
      if (stale.length > 0) draw.delete(stale);
      sync();
    });
    map.on('draw.update', sync);
    map.on('draw.delete', sync);
    setMapLoaded(true);
  }, []);

  // Most venues have no city or state on file, but all have coordinates, so a
  // place search moves the map there and lists the venues nearest to it.
  const goToPlace = async (e: React.FormEvent) => {
    e.preventDefault();
    if (!place.trim()) return;
    setPlaceError(null);
    try {
      const res = await fetch(
        `https://api.mapbox.com/geocoding/v5/mapbox.places/${encodeURIComponent(place.trim())}.json?access_token=${MAPBOX_TOKEN}&country=us&types=place,region,locality,neighborhood,postcode&limit=1`,
      );
      const feature = (await res.json()).features?.[0];
      if (!feature) {
        setPlaceError(`No place found for "${place.trim()}".`);
        return;
      }
      if (feature.bbox) {
        const [west, south, east, north] = feature.bbox;
        mapRef.current?.fitBounds([[west, south], [east, north]], { padding: 40 });
      } else {
        mapRef.current?.flyTo({ center: feature.center, zoom: 12 });
      }
      setSort('nearest');
    } catch {
      setPlaceError('Place search failed.');
    }
  };

  const confirmDiscard = () => !dirty || window.confirm('Discard the unsaved geofence changes?');

  const select = (b: Business) => {
    if (b.id === selectedId || !confirmDiscard()) return;
    setSelectedId(b.id);
    if (b.location_lat != null && b.location_lng != null) {
      mapRef.current?.flyTo({ center: [Number(b.location_lng), Number(b.location_lat)], zoom: 17 });
    }
  };

  const switchEnv = (next: Env) => {
    if (next === env || !confirmDiscard()) return;
    setSelectedId(null);
    setEnv(next);
  };

  // Mid-draw every click places a point, so it must not also pick a venue.
  const handleMapClick = (e: MapMouseEvent) => {
    if (drawRef.current?.getMode() === 'draw_polygon') return;
    const id = e.features?.[0]?.properties?.id;
    const business = businesses.find(b => b.id === id);
    if (business) select(business);
  };

  const clear = () => {
    drawRef.current?.deleteAll();
    setDraft(null);
  };

  const save = async () => {
    if (!selected) return;
    const verb = draft ? 'Update' : 'Remove';
    if (!window.confirm(`${verb} the geofence for ${selected.name} in ${env.toUpperCase()}?`)) return;
    if (await edit(selected.id, { geofence: draft }, apiUrl)) refetch();
  };

  const ring = draft?.coordinates[0] ?? [];
  const vertices = Math.max(ring.length - 1, 0);
  const areaM2 = ring.length > 3 ? ringAreaM2(ring) : 0;
  const tooLarge = areaM2 > MAX_AREA_M2;
  const tooManyPoints = vertices > MAX_VERTICES;
  const pinOutside =
    draft != null &&
    selected?.location_lat != null &&
    selected?.location_lng != null &&
    !ringContains(ring, Number(selected.location_lng), Number(selected.location_lat));
  const missingCount = businesses.filter(b => !polygonOf(b)).length;

  if (!isAdmin) return <Navigate to="/businesses" replace />;

  return (
    <div className="flex bg-gray-950" style={{ height: 'calc(100vh - 72px)' }}>
      <aside className="w-80 flex-shrink-0 flex flex-col border-r border-gray-800 bg-gray-900">
        <div className="p-4 space-y-3 border-b border-gray-800">
          <div className="flex items-center justify-between">
            <h1 className="text-lg font-semibold text-white">Geofences</h1>
            <Link to="/businesses" className="text-xs text-gray-400 hover:text-gray-200">
              ← Admin
            </Link>
          </div>
          <div className="flex items-center gap-1.5 p-1 bg-gray-800/80 border border-gray-700 rounded-xl w-fit">
            {ENVS.map(e => (
              <button
                key={e}
                onClick={() => switchEnv(e)}
                className={`px-3 py-1 text-xs font-medium rounded-lg border transition-all capitalize ${
                  env === e
                    ? ENV_COLORS[e].pill
                    : 'text-gray-400 border-transparent hover:text-gray-200 hover:bg-gray-700/60'
                }`}
              >
                {e}
              </button>
            ))}
          </div>
          <input
            type="text"
            value={search}
            onChange={e => setSearch(e.target.value)}
            placeholder="Search by name or address…"
            className="w-full px-3 py-2 bg-gray-800 border border-gray-700 rounded-lg text-sm text-white placeholder-gray-500 focus:outline-none focus:border-violet-500"
          />
          <form onSubmit={goToPlace}>
            <input
              type="text"
              value={place}
              onChange={e => setPlace(e.target.value)}
              placeholder="Go to city or state, then Enter…"
              className="w-full px-3 py-2 bg-gray-800 border border-gray-700 rounded-lg text-sm text-white placeholder-gray-500 focus:outline-none focus:border-violet-500"
            />
          </form>
          {placeError && <p className="text-xs text-amber-400">{placeError}</p>}
          <select
            value={filter}
            onChange={e => setFilter(e.target.value as Filter)}
            className="w-full px-3 py-2 bg-gray-800 border border-gray-700 rounded-lg text-sm text-white focus:outline-none focus:border-violet-500"
          >
            <option value="all">All businesses ({businesses.length})</option>
            <option value="missing">No geofence ({missingCount})</option>
            <option value="verified">Approved</option>
            <option value="pending">Pending</option>
          </select>
          <select
            value={sort}
            onChange={e => setSort(e.target.value as Sort)}
            className="w-full px-3 py-2 bg-gray-800 border border-gray-700 rounded-lg text-sm text-white focus:outline-none focus:border-violet-500"
          >
            <option value="newest">Sort: newest first</option>
            <option value="name">Sort: name A–Z</option>
            <option value="nearest">Sort: nearest to map centre</option>
          </select>
          {error && <ErrorBanner message={error} />}
          {total > businesses.length && !loading && (
            <ErrorBanner message={`Showing ${businesses.length} of ${total} businesses.`} />
          )}
        </div>

        {selected && (
          <div className="p-4 space-y-3 border-b border-gray-800 bg-gray-950/60">
            <div className="flex items-start justify-between gap-2">
              <div>
                <p className="text-sm font-semibold text-white">{selected.name}</p>
                <p className="text-xs text-gray-500">
                  {selected.verified ? 'Approved' : 'Pending'} · {env}
                </p>
              </div>
              <button
                onClick={() => confirmDiscard() && setSelectedId(null)}
                className="text-xs text-gray-400 hover:text-gray-200"
              >
                Close
              </button>
            </div>

            {draft ? (
              <p className="text-xs text-gray-300">
                {vertices} points ·{' '}
                <span className={tooLarge ? 'text-red-400 font-semibold' : ''}>
                  ≈{Math.round(areaM2).toLocaleString()} m²
                </span>{' '}
                of {MAX_AREA_M2.toLocaleString()} m²
              </p>
            ) : (
              <p className="text-xs text-amber-400">
                {original
                  ? 'Geofence removed. Saving leaves the venue with no live check-in boundary.'
                  : 'No geofence. Click the map to draw one; double-click to finish.'}
              </p>
            )}
            {tooLarge && <p className="text-xs text-red-400">{SAVE_ERRORS.GEOFENCE_TOO_LARGE}</p>}
            {tooManyPoints && <p className="text-xs text-red-400">{SAVE_ERRORS.GEOFENCE_TOO_MANY_POINTS}</p>}
            {pinOutside && (
              <p className="text-xs text-amber-400">The venue's pin is outside this polygon. You can still save.</p>
            )}
            {saveError && <ErrorBanner message={SAVE_ERRORS[saveError] ?? saveError} />}

            <div className="flex items-center gap-2">
              <button
                onClick={save}
                disabled={!dirty || saving || tooLarge || tooManyPoints}
                className="flex items-center gap-2 px-3 py-1.5 text-xs font-medium rounded-lg bg-violet-600 text-white hover:bg-violet-500 disabled:opacity-40 disabled:cursor-not-allowed"
              >
                {saving && <Spinner size="sm" />}
                Save
              </button>
              <button
                onClick={() => setRevertTick(t => t + 1)}
                disabled={!dirty || saving}
                className="px-3 py-1.5 text-xs font-medium rounded-lg border border-gray-700 text-gray-300 hover:text-white disabled:opacity-40"
              >
                Revert
              </button>
              <button
                onClick={clear}
                disabled={!draft || saving}
                className="px-3 py-1.5 text-xs font-medium rounded-lg border border-red-900/60 text-red-300 hover:text-red-200 disabled:opacity-40"
              >
                Clear
              </button>
            </div>
          </div>
        )}

        <div className="flex-1 overflow-y-auto">
          {loading && businesses.length === 0 ? (
            <div className="flex justify-center py-8">
              <Spinner />
            </div>
          ) : (
            visible.map(b => (
              <button
                key={b.id}
                onClick={() => select(b)}
                className={`w-full flex items-center justify-between gap-2 px-4 py-2 text-left text-sm border-b border-gray-800/60 hover:bg-gray-800/60 ${
                  b.id === selectedId ? 'bg-gray-800 text-white' : 'text-gray-300'
                }`}
              >
                <span className="truncate">{b.name}</span>
                {!polygonOf(b) && <span className="flex-shrink-0 text-[10px] text-amber-400">no geofence</span>}
              </button>
            ))
          )}
        </div>
      </aside>

      <div className="flex-1 relative">
        {MAPBOX_TOKEN ? (
          <Map
            ref={mapRef}
            mapboxAccessToken={MAPBOX_TOKEN}
            initialViewState={{ longitude: -98.5795, latitude: 39.8283, zoom: 3.5 }}
            mapStyle="mapbox://styles/mapbox/dark-v11"
            interactiveLayerIds={['business-points', 'fence-fill']}
            cursor={cursor}
            onMouseEnter={() => setCursor('pointer')}
            onMoveEnd={e => setCenter({ lng: e.viewState.longitude, lat: e.viewState.latitude })}
            onMouseLeave={() => setCursor('')}
            onClick={handleMapClick}
            onLoad={handleMapLoad}
          >
            <NavigationControl position="top-right" />
            <Source id="fences" type="geojson" data={fences}>
              <Layer
                id="fence-fill"
                type="fill"
                filter={['!=', ['get', 'id'], selectedId ?? '']}
                paint={{ 'fill-color': '#8b5cf6', 'fill-opacity': 0.15 }}
              />
              <Layer
                id="fence-line"
                type="line"
                filter={['!=', ['get', 'id'], selectedId ?? '']}
                paint={{ 'line-color': '#a78bfa', 'line-width': 1.5 }}
              />
            </Source>
            <Source id="businesses" type="geojson" data={points}>
              <Layer
                id="business-points"
                type="circle"
                paint={{
                  'circle-radius': 6,
                  'circle-color': ['case', ['get', 'missing'], '#f59e0b', '#8b5cf6'],
                  'circle-stroke-color': '#111827',
                  'circle-stroke-width': 1.5,
                }}
              />
            </Source>
          </Map>
        ) : (
          <div className="p-6">
            <ErrorBanner message="Mapbox token is not configured." />
          </div>
        )}
        <div className="absolute bottom-4 left-4 flex items-center gap-3 px-3 py-1.5 rounded-lg bg-gray-900/90 border border-gray-800 text-xs text-gray-300">
          <span className="flex items-center gap-1.5">
            <span className="w-2.5 h-2.5 rounded-full bg-violet-500" /> Has geofence
          </span>
          <span className="flex items-center gap-1.5">
            <span className="w-2.5 h-2.5 rounded-full bg-amber-500" /> No geofence
          </span>
        </div>
      </div>
    </div>
  );
}
